import { useState } from 'react'
import { Box, Flex, Grid, Text, Input, useT } from '@minetrace/ui'
import { Controller, useFormColors, FormSection, FormLabel, useWatch } from '@minetrace/ui/forms'
import { LuCalendar as Calendar, LuUpload as Upload, LuFileText as FileIcon, LuTrash2 as Trash } from 'react-icons/lu'
import { toaster } from '@minetrace/ui'
import FormDatePicker from '../../../components/FormDatePicker'
import { programacionesService } from '../../../api/services'
import type { Control, FieldErrors } from 'react-hook-form'
import type { ScheduleDocumentResponse } from '../../../api/models/response/programacion'
import ScheduleDocUploadModal from './ScheduleDocUploadModal'
import CharCounter from '../../../components/CharCounter'
import { UiFieldLengths } from '../../../lib/fieldLengths'

export interface Step1FormData {
  name?: string
  startDate: string
  endDate: string
  includeOcdeAudit: boolean
  ocdeAuditRequired: boolean
}

export interface PendingFile {
  id: string
  file: File
  notes: string
}

interface Step1Props {
  control: Control<Step1FormData>
  errors: FieldErrors<Step1FormData>
  code: string | null
  scheduleId: string | null
  documents: ScheduleDocumentResponse[]
  onDocumentsChange: (docs: ScheduleDocumentResponse[]) => void
  pendingFiles: PendingFile[]
  onPendingFilesChange: (files: PendingFile[]) => void
}

interface PillToggleProps {
  checked: boolean
  onChange: (value: boolean) => void
  disabled?: boolean
}

const PillToggle = ({ checked, onChange, disabled }: PillToggleProps) => (
  <Box
    role="switch"
    aria-checked={checked}
    aria-disabled={disabled}
    onClick={() => !disabled && onChange(!checked)}
    w="44px"
    h="24px"
    borderRadius="full"
    bg={disabled ? '#cbd5e1' : checked ? '#1d6664' : '#cbd5e1'}
    opacity={disabled ? 0.6 : 1}
    cursor={disabled ? 'not-allowed' : 'pointer'}
    position="relative"
    flexShrink={0}
  >
    <Box
      position="absolute"
      top="3px"
      left={checked ? '23px' : '3px'}
      w="18px"
      h="18px"
      borderRadius="full"
      bg="white"
      shadow="0 1px 2px rgba(0,0,0,0.25)"
    />
  </Box>
)

const Step1DatosGenerales = ({ control, errors, code, scheduleId, documents, onDocumentsChange, pendingFiles, onPendingFilesChange }: Step1Props) => {
  const c = useFormColors()
  const { t } = useT()
  const [uploadModalOpen, setUploadModalOpen] = useState(false)
  const [removingId, setRemovingId] = useState<string | null>(null)

  const handleUpload = async (formData: FormData) => {
    const file = formData.get('file') as File
    const notes = (formData.get('notes') as string) ?? ''

    if (scheduleId) {
      const doc = await programacionesService.uploadDocument(scheduleId, formData)
      onDocumentsChange([...documents, doc])
    } else {
      onPendingFilesChange([...pendingFiles, { id: crypto.randomUUID(), file, notes }])
    }
  }

  const handleRemovePending = (fileId: string) => {
    onPendingFilesChange(pendingFiles.filter((f) => f.id !== fileId))
  }

  const handleRemove = async (documentId: string) => {
    if (!scheduleId) return
    setRemovingId(documentId)
    try {
      await programacionesService.removeDocument(scheduleId, documentId)
      onDocumentsChange(documents.filter((d) => d.id !== documentId))
      toaster.success({ description: t('programaciones.wizard.toasts.documentDeleted') })
    } catch {
      toaster.error({ description: t('programaciones.wizard.toasts.documentDeleteError') })
    } finally {
      setRemovingId(null)
    }
  }

  const today = new Date().toISOString().split('T')[0]
  const watchedStartDate = useWatch({ control, name: 'startDate' })
  const watchedIncludeOcde = useWatch({ control, name: 'includeOcdeAudit' })

  return (
    <FormSection icon={Calendar} titleTid="programaciones.wizard.step1.sectionTitle" descriptionTid="programaciones.wizard.step1.sectionDescription">
      {code && (
        <Box mb={4} p={3} bg={c.bgInput} borderRadius="xl">
          <Text tid="programaciones.wizard.step1.codeLabel" fontSize="xs" fontWeight="600" color={c.textMuted} textTransform="uppercase" letterSpacing="0.05em" />
          <Text fontSize="md" fontWeight="700" color={c.text} fontFamily="monospace">
            {code}
          </Text>
        </Box>
      )}

      <Box mb={4}>
        <FormLabel tid="programaciones.wizard.step1.nameLabel" />
        <Controller
          name="name"
          control={control}
          render={({ field }) => (
            <Input
              value={field.value ?? ''}
              onChange={(e) => field.onChange(e.target.value.slice(0, UiFieldLengths.shortNote))}
              placeholder={t('programaciones.wizard.step1.namePlaceholder')}
              maxLength={UiFieldLengths.shortNote}
              bg={c.bgInput}
              border="1px solid"
              borderColor={c.border}
              borderRadius="xl"
              color={c.text}
              _placeholder={{ color: c.textMuted }}
              _focus={{ borderColor: '#b4532a', ring: '1px', ringColor: '#b4532a' }}
            />
          )}
        />
        <CharCounter name="name" max={UiFieldLengths.shortNote} />
      </Box>

      <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={6}>
        <Box>
          <FormLabel tid="programaciones.wizard.step1.startDateLabel" required />
          <Controller
            name="startDate"
            control={control}
            render={({ field }) => (
              <FormDatePicker
                value={field.value ?? ''}
                onChange={field.onChange}
                placeholder={t('programaciones.wizard.step1.startDatePlaceholder')}
                minDate={today}
              />
            )}
          />
          {errors.startDate && (
            <Text fontSize="xs" color={c.error} mt={1}>{t(errors.startDate.message ?? '')}</Text>
          )}
        </Box>

        <Box>
          <FormLabel tid="programaciones.wizard.step1.endDateLabel" required />
          <Controller
            name="endDate"
            control={control}
            render={({ field }) => (
              <FormDatePicker
                value={field.value ?? ''}
                onChange={field.onChange}
                placeholder={t('programaciones.wizard.step1.endDatePlaceholder')}
                minDate={watchedStartDate || today}
              />
            )}
          />
          {errors.endDate && (
            <Text fontSize="xs" color={c.error} mt={1}>{t(errors.endDate.message ?? '')}</Text>
          )}
        </Box>
      </Grid>

      {/* Auditoría OCDE */}
      <Box mt={8} p={4} bg={c.bgInput} borderRadius="xl" border="1px solid" borderColor={c.border}>
        <Flex justifyContent="space-between" alignItems="flex-start" gap={4}>
          <Box>
            <Text fontSize="sm" fontWeight="700" color={c.text}>
              Cumplimiento Normativo
            </Text>
            <Text fontSize="xs" color={c.textMuted} mt={1}>
              Agregar el cuestionario de Cumplimiento Normativo (33 preguntas en 6 categorías) a cada visita de esta programación.
            </Text>
          </Box>
          <Controller
            name="includeOcdeAudit"
            control={control}
            render={({ field }) => (
              <PillToggle
                checked={!!field.value}
                onChange={(v) => {
                  field.onChange(v)
                }}
              />
            )}
          />
        </Flex>

        {watchedIncludeOcde && (
          <Flex justifyContent="space-between" alignItems="flex-start" gap={4} mt={4} pt={4} borderTop="1px solid" borderColor={c.border}>
            <Box>
              <Text fontSize="sm" fontWeight="600" color={c.text}>
                Cuestionario obligatorio
              </Text>
              <Text fontSize="xs" color={c.textMuted} mt={1}>
                El fiscalizador deberá responder las 33 preguntas con comentario y foto para poder cerrar la visita; las respuestas se incluyen en el acta.
              </Text>
            </Box>
            <Controller
              name="ocdeAuditRequired"
              control={control}
              render={({ field }) => (
                <PillToggle checked={!!field.value} onChange={field.onChange} />
              )}
            />
          </Flex>
        )}
      </Box>

      {/* Documentacion Complementaria */}
      <Box mt={8}>
        <Flex justifyContent="space-between" alignItems="center" mb={3}>
          <Box>
            <Text tid="programaciones.wizard.step1.docsTitle" fontSize="sm" fontWeight="700" color={c.text} />
            <Text tid="programaciones.wizard.step1.docsDescription" fontSize="xs" color={c.textMuted} />
          </Box>
          <Box
            as="button"
            type="button"
            px={4}
            h="36px"
            borderRadius="lg"
            fontSize="sm"
            fontWeight="600"
            bg="#b4532a"
            color="white"
            cursor="pointer"
            _hover={{ bg: '#085a30' }}
            display="flex"
            alignItems="center"
            gap={2}
            onClick={() => setUploadModalOpen(true)}
          >
            <Upload size={14} />
            {t('programaciones.wizard.step1.uploadButton')}
          </Box>
        </Flex>

        {documents.length === 0 && pendingFiles.length === 0 ? (
          <Box p={4} textAlign="center" bg={c.bgInput} borderRadius="lg">
            <Text tid="programaciones.wizard.step1.noDocs" fontSize="sm" color={c.textMuted} />
          </Box>
        ) : (
          <Box display="flex" flexDirection="column" gap={2}>
            {documents.map((doc) => (
              <Flex
                key={doc.id}
                p={3}
                bg={c.bgInput}
                borderRadius="lg"
                alignItems="center"
                justifyContent="space-between"
              >
                <Flex alignItems="center" gap={3} flex={1} minW={0}>
                  <FileIcon size={18} color="#b4532a" />
                  <Box minW={0}>
                    <Text fontSize="sm" fontWeight="600" color={c.text} truncate title={doc.fileName ?? undefined}>
                      {doc.fileName ?? t('programaciones.wizard.step1.defaultDocName')}
                    </Text>
                    {doc.notes && (
                      <Text fontSize="xs" color={c.textMuted}>{doc.notes}</Text>
                    )}
                    {doc.uploadedAt && (
                      <Text fontSize="xs" color={c.textMuted}>
                        {new Date(doc.uploadedAt).toLocaleDateString('es-PE')}
                      </Text>
                    )}
                  </Box>
                </Flex>
                <Box
                  as="button"
                  type="button"
                  p={2}
                  borderRadius="lg"
                  color="red.500"
                  cursor={removingId === doc.id ? 'not-allowed' : 'pointer'}
                  opacity={removingId === doc.id ? 0.5 : 1}
                  _hover={{ bg: 'red.50' }}
                  onClick={() => handleRemove(doc.id)}
                  disabled={removingId === doc.id}
                >
                  <Trash size={16} />
                </Box>
              </Flex>
            ))}

            {pendingFiles.map((pf) => (
              <Flex
                key={pf.id}
                p={3}
                bg="#fff9e6"
                border="1px dashed #d4a017"
                borderRadius="lg"
                alignItems="center"
                justifyContent="space-between"
              >
                <Flex alignItems="center" gap={3} flex={1} minW={0}>
                  <FileIcon size={18} color="#d4a017" />
                  <Box minW={0}>
                    <Text fontSize="sm" fontWeight="600" color={c.text} truncate title={pf.file.name}>
                      {pf.file.name}
                    </Text>
                    {pf.notes && <Text fontSize="xs" color={c.textMuted}>{pf.notes}</Text>}
                    <Text tid="programaciones.wizard.step1.pendingUpload" fontSize="xs" color="#d4a017" fontWeight="500" />
                  </Box>
                </Flex>
                <Box
                  as="button" type="button" p={2} borderRadius="lg"
                  color="red.500" cursor="pointer" _hover={{ bg: 'red.50' }}
                  onClick={() => handleRemovePending(pf.id)}
                >
                  <Trash size={16} />
                </Box>
              </Flex>
            ))}
          </Box>
        )}
      </Box>

      <ScheduleDocUploadModal
        open={uploadModalOpen}
        onClose={() => setUploadModalOpen(false)}
        onUpload={handleUpload}
      />
    </FormSection>
  )
}

export default Step1DatosGenerales
